/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { LBCHomePage } from "./Templates/HomePage";
import {
  ChapterCover,
  ChapterDetailsLoadingSkeleton,
  Footer,
  Header,
  NotLoggedInCard,
} from "./Components/organisms";
import { Container, GradientDivider } from "./Components/atoms";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  DialogPopup,
  JoinSuccessPopup,
  LocPerPopupContent,
  NoChapAvailablePopup,
  NotEligiblePopup,
} from "./Components/molecules";
import { LoginPopup } from "../CreatorLandingPage/CreatorLandingPage";
import { useToast } from "../../../shadcnComponents/ui/use-toast";
import { useStaticData } from "../../../Providers/Data";
import { useInfluencer } from "../../../Providers/Influencer";
import mixpanel from "mixpanel-browser";
import { useAuth } from "../../../Providers/Auth";

const host = import.meta.env.VITE_BACKEND_SERVER_URL;

interface ChapterDataTypes {
  chapterDetails?: {
    _id?: string;
    city?: string;
    cover?: string;
    logo?: string;
    pageDesc?: string;
    shortDesc?: string;
    title?: string;
    slug?: string;
  };
  joinedCreators?: {
    count?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    influencers?: any[];
    avatars?: string[];
  };
  suggestedChapter?: {
    chapterId?: string;
    chapterTitle?: string;
    joined?: {
      chapterId?: string;
    };
    slug?: string;
  };
  isCorrectChapter?: boolean;
  isChapterAvailable?: boolean;
  isLogin?: boolean;
  isJoined?: boolean;
  userCity?: string;
}

export const LocationBasedChaptersRedirect = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const influencerState = useInfluencer();

  useEffect(() => {
    const fetchChapterDetails = async () => {
      try {
        const data = await influencerState?.getChapterDetails({
          chapterId: chapterId,
          ipAddress: "",
          geoCoordinates: {},
          geoLocation: {},
        });

        if (data?.success && data?.slug) {
          navigate(`/chapter/${data?.slug}`, { replace: true });
        } else {
          navigate("/influencer", { replace: true });
        }
      } catch {
        navigate("/influencer", { replace: true });
      }
    };

    if (chapterId) {
      fetchChapterDetails();
    }
  }, [chapterId, navigate]);

  return null;
};

export const LocationBasedChaptersV1 = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const influencerState = useInfluencer();
  const [isLogin, setIsLogin] = useState(false);
  const [hasChapter, setHasChapter] = useState(false);
  const [chapterData, setChapterData] = useState<ChapterDataTypes>();
  const [ipAddress, setIpAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingJoin, setLoadingJoin] = useState(false);
  const [openLocPerPopup, setOpenLocPerPopup] = useState(false); // Location Permission Popup
  const [openSugChPopup, setOpenSugChPopup] = useState(false); // Suggested Chapter Popup
  const [openLoginPopup, setOpenLoginPopup] = useState(false); // Login Popup
  const [openNoChapPopup, setOpenNoChapPopup] = useState(false); // No Chapter Available Popup
  const [openJoinSuccessPopup, setOpenJoinSuccessPopup] = useState(false); // Join Success Popup
  const [geoLocation, setGeoLocation] = useState({
    latitude: 0,
    longitude: 0,
    city: "",
    state: "",
    country: "",
    postalCode: "",
  });

  const [geoCoordinates, setGeoCoordinates] = useState({
    latitude: 0,
    longitude: 0,
    permission: false,
  });

  //master data
  const staticDataState = useStaticData();
  const [masterContentCategory, setMasterContentCategory] = useState([]);

  useEffect(() => {
    staticDataState?.getNewDataFromType("Content Type").then((e) => {
      setMasterContentCategory(e);
    });
  }, []);

  const geoLocationPermission = () => {
    return new Promise<void>((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setGeoCoordinates({ latitude, longitude, permission: true });
            setOpenLocPerPopup(false);
            setOpenSugChPopup(false);
            resolve();
          },
          (error) => {
            setGeoCoordinates({ latitude: 0, longitude: 0, permission: false });
            reject(error);
          }
        );
      } else {
        alert("Geolocation is not supported by this browser.");
        reject(new Error("Geolocation not supported"));
      }
    });
  };

  const fetchChapterDetails = async () => {
    try {
      const data = await influencerState?.getChapterDetails({
        chapterId: chapterId,
        ipAddress: ipAddress,
        geoCoordinates: geoCoordinates,
        geoLocation: geoLocation,
      });

      if (data?.success) {
        if (data?.joinedCreators?.influencers?.length > 0) {
          data.joinedCreators.influencers.forEach((influencer) => {
            const contentCategories = influencer?.contentCategories;
            if (contentCategories?.length > 0) {
              const contentCategoryNames = contentCategories?.map(
                (contentCategory) => {
                  const contentCategoryName =
                    masterContentCategory.find(
                      (masterContentCategory) =>
                        masterContentCategory._id === contentCategory
                    )?.categoryName || contentCategory;
                  return contentCategoryName;
                }
              );
              influencer.mappedCategories = contentCategoryNames;
            }
          });
        }
        setHasChapter(true);
        setIsLogin(data.isLogin);
        setChapterData(data || null);
        setLoading(false);
      } else {
        navigate("/influencer", { replace: true });
      }
    } catch {
      navigate("/influencer", { replace: true });
    }
  };

  const joinChapter = async () => {
    try {
      setLoadingJoin(true);
      const res = await axios.post(
        `${host}/influencer/chapter/${chapterId}/join`,
        {
          ip: ipAddress,
          lat: geoCoordinates?.permission
            ? geoCoordinates?.latitude
            : geoLocation?.latitude,
          lon: geoCoordinates?.permission
            ? geoCoordinates?.longitude
            : geoLocation?.longitude,
          city: geoLocation?.city,
          state: geoLocation?.state,
          country: geoLocation?.country,
          postalCode: geoLocation?.postalCode,
          geoLocation: geoCoordinates?.permission,
        },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      if (res?.data?.success) {
        setChapterData((prev) => ({
          ...prev,
          isJoined: true,
        }));
        await fetchChapterDetails();
        setOpenJoinSuccessPopup(true);
      } else {
        toast({
          title: res?.data?.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error In Joining Chapter",
        variant: "destructive",
      });
    } finally {
      setLoadingJoin(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("https://api.ipify.org/?format=json");
        const data = res?.data;
        setIpAddress(data?.ip);
      } catch {
        console.log("Error in getting IP Address");
      } finally {
        await geoLocationPermission();
      }
    })();
  }, []);

  useEffect(() => {
    const fetchGeoLocation = async () => {
      if (!ipAddress) return;

      try {
        const res = await axios.get(`https://ipinfo.io/${ipAddress}/json`);
        const data = res?.data;
        setGeoLocation((prev) => ({
          ...prev,
          latitude: geoCoordinates?.permission
            ? geoCoordinates?.latitude
            : data?.loc?.split(",")?.[0],
          longitude: geoCoordinates?.permission
            ? geoCoordinates?.longitude
            : data?.loc?.split(",")?.[1],
          city: data?.city,
          state: data?.region,
          country: data?.country,
          postalCode: data?.postal,
        }));
      } catch {
        console.log("Error in getting GeoLocation");
      }
    };

    fetchGeoLocation();
  }, [ipAddress]);

  useEffect(() => {
    if (chapterId && ipAddress && geoLocation?.city) {
      fetchChapterDetails();
    }
  }, [chapterId, ipAddress, geoLocation, geoCoordinates]);

  useEffect(() => {
    if (
      chapterData?.isLogin &&
      localStorage.getItem("loginFCID") === chapterId
    ) {
      // login from chapter id
      localStorage.removeItem("loginFCID");
      handleJoinChapter();
      toast({
        title: "Logged In Successfully",
        className: "bg-green-600 text-white",
      });
    }
  }, [chapterData, chapterId, toast]);

  const handleJoinChapter = async () => {
    if (!geoCoordinates?.permission) {
      setOpenLocPerPopup(true);
      try {
        await geoLocationPermission();
        if (!geoCoordinates?.permission) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error?.code === 1) {
          console.log("User denied the request for Geolocation.");
        }
        return;
      }
    }

    if (!isLogin) {
      setOpenLoginPopup(true);
      localStorage.setItem("loginFCID", chapterId);
      return;
    }

    if (
      !chapterData?.isCorrectChapter &&
      chapterData?.suggestedChapter?.chapterId
    ) {
      setOpenSugChPopup(true);
      return;
    }

    if (!hasChapter || !chapterData?.isChapterAvailable) {
      setOpenNoChapPopup(true);
      return;
    }

    await joinChapter();
  };

  const handleRetryAllowLocation = async () => {
    setOpenLocPerPopup(false);
  };

  const handleShareChapter = async () => {
    navigator.share({
      text: `I've joined this Chapter; it's for creators living in Noida only. If you're in Noida too, do join, so we can all find each other and plan something exciting for some weekend! :D  https://collab.anchors.in/influencer/${chapterId}/chapter`, //chapter description
    });
  };

  return (
    <>
      {
        /* Location Permission Popup */
        openLocPerPopup && (
          <DialogPopup open={openLocPerPopup} onOpenChange={setOpenLocPerPopup}>
            <LocPerPopupContent
              onRetry={() => {
                mixpanel.track(
                  "Creator Chapter: Location Popup: Got it Button CLicked"
                );
                handleRetryAllowLocation();
              }}
            />
          </DialogPopup>
        )
      }

      {openLoginPopup && (
        <LoginPopup onClose={() => setOpenLoginPopup(false)} backToSource />
      )}

      {
        /* Not Eligible or Suggested Chapter Popup */
        openSugChPopup && (
          <DialogPopup open={openSugChPopup} onOpenChange={setOpenSugChPopup}>
            <NotEligiblePopup
              suggestedChapterTitle={
                chapterData?.suggestedChapter?.chapterTitle
              }
              chapterTitle={chapterData?.chapterDetails?.title}
              handleViewSuggestedChapter={() => {
                mixpanel.track(
                  "Creator Chapter: Not Eligible or Suggested Chapter: Join Button Clicked"
                );
                window.location.href = `/influencer/${chapterData?.suggestedChapter?.chapterId}/chapter`;
              }}
            />
          </DialogPopup>
        )
      }

      {openNoChapPopup && (
        <DialogPopup open={openNoChapPopup} onOpenChange={setOpenNoChapPopup}>
          <NoChapAvailablePopup
            onClickExplore={() => {
              mixpanel.track(
                "Creator Chapter: No Chapter Available: Explore Button Clicked"
              );
              navigate("/influencer/dashboard");
            }}
          />
        </DialogPopup>
      )}

      {
        /* Join Success Popup */
        openJoinSuccessPopup && (
          <DialogPopup
            open={openJoinSuccessPopup}
            onOpenChange={setOpenJoinSuccessPopup}
          >
            <JoinSuccessPopup
              chapterTitle={chapterData?.chapterDetails?.title}
              onClickExplore={() => {
                mixpanel.track(
                  "Creator Chapter: Success Popup: Explore Button Clicked"
                );
                navigate("/influencer/dashboard");
              }}
            />
          </DialogPopup>
        )
      }

      <div className="w-full h-full min-h-screen bg-[#171717] text-white flex flex-col ">
        {/* Header */}
        <Header bg={"bg-[#171717]"} hideUser={!isLogin} hideName={true} />

        {/* Main */}
        <div className="flex-1">
          {hasChapter && !loading ? (
            <LBCHomePage
              isLogin={isLogin}
              chapterDetails={chapterData?.chapterDetails}
              influencers={chapterData?.joinedCreators?.influencers}
              chapterCover={
                <ChapterCover
                  chapterDetails={chapterData?.chapterDetails}
                  avatars={chapterData?.joinedCreators?.avatars}
                  totalCount={chapterData?.joinedCreators?.count}
                  onJoinChapter={() => {
                    mixpanel.track(
                      "Creator Chapter: Join Chapter Button Clicked"
                    );
                    handleJoinChapter();
                  }}
                  isJoined={chapterData?.isJoined}
                  loadingJoin={loadingJoin}
                  onShare={() => {
                    mixpanel.track("Creator Chapter: Invite Button Clicked");
                    handleShareChapter();
                  }}
                />
              }
              NotLoggedInCard={
                <NotLoggedInCard
                  userCity={chapterData?.chapterDetails?.city}
                  handleJoinChapter={handleJoinChapter}
                />
              }
            />
          ) : loading ? (
            <ChapterDetailsLoadingSkeleton />
          ) : (
            <div>Chapter Not Found</div>
          )}
        </div>

        {/* Footer */}
        <div className="w-full">
          <Container>
            <GradientDivider className="h-[1px]" />
            <Footer />
          </Container>
        </div>
      </div>
    </>
  );
};

export const LocationBasedChapters = () => {
  const { chapterSlug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const authState = useAuth();
  const influencerState = useInfluencer();
  const [isLogin, setIsLogin] = useState(false);
  const [hasChapter, setHasChapter] = useState(false);
  const [chapterData, setChapterData] = useState<ChapterDataTypes>();
  const [ipAddress, setIpAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingJoin, setLoadingJoin] = useState(false);
  const [openLocPerPopup, setOpenLocPerPopup] = useState(false); // Location Permission Popup
  const [openSugChPopup, setOpenSugChPopup] = useState(false); // Suggested Chapter Popup
  const [openLoginPopup, setOpenLoginPopup] = useState(false); // Login Popup
  const [openNoChapPopup, setOpenNoChapPopup] = useState(false); // No Chapter Available Popup
  const [openJoinSuccessPopup, setOpenJoinSuccessPopup] = useState(false); // Join Success Popup
  const [geoLocation, setGeoLocation] = useState({
    latitude: 0,
    longitude: 0,
    city: "",
    state: "",
    country: "",
    postalCode: "",
  });

  const [geoCoordinates, setGeoCoordinates] = useState({
    latitude: 0,
    longitude: 0,
    permission: false,
  });

  const [chapterId, setChapterId] = useState(null);

  //master data
  const staticDataState = useStaticData();
  const [masterContentCategory, setMasterContentCategory] = useState([]);

  useEffect(() => {
    staticDataState?.getNewDataFromType("Content Type").then((e) => {
      setMasterContentCategory(e);
    });
  }, []);

  useEffect(() => {
    // get network ip & location
    if (authState?.getUserLocation) {
      authState?.getUserLocation().then((location) => {
        setIpAddress(location?.ip);
        setGeoLocation({
          latitude: parseFloat(location?.lat),
          longitude: parseFloat(location?.lon),
          city: location?.city,
          state: location?.state,
          country: location?.country,
          postalCode: location?.postalCode,
        });
      });

      geoLocationPermission();
    }
  }, []);

  const geoLocationPermission = () => {
    return new Promise<void>((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setGeoCoordinates({ latitude, longitude, permission: true });
            setOpenLocPerPopup(false);
            setOpenSugChPopup(false);
            resolve();
          },
          (error) => {
            setGeoCoordinates({ latitude: 0, longitude: 0, permission: false });
            reject(error);
          }
        );
      } else {
        alert("Geolocation is not supported by this browser.");
        reject(new Error("Geolocation not supported"));
      }
    });
  };

  const fetchChapterDetails = async () => {
    try {
      const data = await influencerState?.getChapterDetailsV2({
        chapterSlug: chapterSlug,
        ipAddress: ipAddress,
        geoCoordinates: geoCoordinates,
        geoLocation: geoLocation,
      });

      if (data?.success) {
        // if (data?.joinedCreators?.influencers?.length > 0) {
        //   data.joinedCreators.influencers.forEach((influencer) => {
        //     const contentCategories = influencer?.contentCategories;
        //     if (contentCategories?.length > 0) {
        //       const contentCategoryNames = contentCategories?.map(
        //         (contentCategory) => {
        //           const contentCategoryName =
        //             masterContentCategory?.find(
        //               (masterContentCategory) =>
        //                 masterContentCategory._id === contentCategory
        //             )?.categoryName || contentCategory;
        //           return contentCategoryName;
        //         }
        //       );
        //       influencer.mappedCategories = contentCategoryNames;
        //     }
        //   });
        // }
        setChapterId(data?.chapterDetails?._id);
        setHasChapter(true);
        setIsLogin(data?.isLogin);
        setChapterData(data || null);
        setLoading(false);
      } else {
        navigate("/influencer", { replace: true });
      }
    } catch {
      navigate("/influencer", { replace: true });
    }
  };

  const joinChapter = async () => {
    try {
      setLoadingJoin(true);
      const res = await axios.post(
        `${host}/influencer/chapter/${chapterId}/join`,
        {
          ip: ipAddress,
          lat: geoCoordinates?.permission
            ? geoCoordinates?.latitude
            : geoLocation?.latitude,
          lon: geoCoordinates?.permission
            ? geoCoordinates?.longitude
            : geoLocation?.longitude,
          city: geoLocation?.city,
          state: geoLocation?.state,
          country: geoLocation?.country,
          postalCode: geoLocation?.postalCode,
          geoLocation: geoCoordinates?.permission,
        },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
            "x-geo": geoCoordinates?.permission,
          },
        }
      );
      if (res?.data?.success) {
        setChapterData((prev) => ({
          ...prev,
          isJoined: true,
        }));
        await fetchChapterDetails();
        setOpenJoinSuccessPopup(true);
      } else {
        toast({
          title: res?.data?.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error In Joining Chapter",
        variant: "destructive",
      });
    } finally {
      setLoadingJoin(false);
    }
  };

  useEffect(() => {
    if (chapterSlug && ipAddress && geoLocation?.city) {
      fetchChapterDetails();
    }
  }, [chapterSlug, ipAddress, geoLocation, geoCoordinates]);

  useEffect(() => {
    if (
      chapterData?.isLogin &&
      localStorage.getItem("loginFCID") === chapterId
    ) {
      // login from chapter id
      localStorage.removeItem("loginFCID");
      handleJoinChapter();
      toast({
        title: "Logged In Successfully",
        className: "bg-green-600 text-white",
      });
    }
  }, [chapterData, chapterId, toast]);

  useEffect(() => {
    if (chapterData?.joinedCreators?.influencers?.length > 0) {
      const updatedInfluencers = chapterData.joinedCreators.influencers.map(
        (influencer) => {
          const contentCategories = influencer?.contentCategories;
          if (contentCategories?.length > 0) {
            const contentCategoryNames = contentCategories.map(
              (contentCategory) => {
                const contentCategoryName =
                  masterContentCategory.find(
                    (masterContentCategory) =>
                      masterContentCategory._id === contentCategory
                  )?.categoryName || contentCategory;
                return contentCategoryName;
              }
            );
            return {
              ...influencer,
              mappedCategories: contentCategoryNames,
            };
          }
          return influencer;
        }
      );

      setChapterData((prev) => ({
        ...prev,
        joinedCreators: {
          ...prev.joinedCreators,
          influencers: updatedInfluencers,
        },
      }));
    }
  }, [chapterData, masterContentCategory]);

  const handleJoinChapter = async () => {
    if (!geoCoordinates?.permission) {
      setOpenLocPerPopup(true);
      try {
        await geoLocationPermission();
        if (!geoCoordinates?.permission) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error?.code === 1) {
          console.log("User denied the request for Geolocation.");
        }
        return;
      }
    }

    if (!isLogin) {
      setOpenLoginPopup(true);
      localStorage.setItem("loginFCID", chapterId);
      return;
    }

    if (
      !chapterData?.isCorrectChapter &&
      chapterData?.suggestedChapter?.chapterId
    ) {
      setOpenSugChPopup(true);
      return;
    }

    if (!hasChapter || !chapterData?.isChapterAvailable) {
      setOpenNoChapPopup(true);
      return;
    }

    await joinChapter();
  };

  const handleRetryAllowLocation = async () => {
    setOpenLocPerPopup(false);
  };

  const handleShareChapter = async () => {
    const message = `I've joined this Chapter; it's for creators living in ${chapterData?.chapterDetails?.city} only. If you're in ${chapterData?.chapterDetails?.city} too, do join, so we can all find each other and plan something exciting for some weekend! :D  https://collab.anchors.in/chapter/${chapterSlug}`;
    navigator.clipboard.writeText(message);
    navigator.share({
      text: message,
    });
  };

  return (
    <>
      {
        /* Location Permission Popup */
        openLocPerPopup && (
          <DialogPopup open={openLocPerPopup} onOpenChange={setOpenLocPerPopup}>
            <LocPerPopupContent
              onRetry={() => {
                mixpanel.track(
                  "Creator Chapter: Location Popup: Got it Button CLicked"
                );
                handleRetryAllowLocation();
              }}
            />
          </DialogPopup>
        )
      }

      {openLoginPopup && (
        <LoginPopup onClose={() => setOpenLoginPopup(false)} backToSource />
      )}

      {
        /* Not Eligible or Suggested Chapter Popup */
        openSugChPopup && (
          <DialogPopup open={openSugChPopup} onOpenChange={setOpenSugChPopup}>
            <NotEligiblePopup
              suggestedChapterTitle={
                chapterData?.suggestedChapter?.chapterTitle
              }
              chapterTitle={chapterData?.chapterDetails?.title}
              handleViewSuggestedChapter={() => {
                mixpanel.track(
                  "Creator Chapter: Not Eligible or Suggested Chapter: Join Button Clicked"
                );
                window.location.href = `/chapter/${chapterData?.suggestedChapter?.slug}`;
              }}
            />
          </DialogPopup>
        )
      }

      {openNoChapPopup && (
        <DialogPopup open={openNoChapPopup} onOpenChange={setOpenNoChapPopup}>
          <NoChapAvailablePopup
            onClickExplore={() => {
              mixpanel.track(
                "Creator Chapter: No Chapter Available: Explore Button Clicked"
              );
              navigate("/influencer/dashboard");
            }}
          />
        </DialogPopup>
      )}

      {
        /* Join Success Popup */
        openJoinSuccessPopup && (
          <DialogPopup
            open={openJoinSuccessPopup}
            onOpenChange={setOpenJoinSuccessPopup}
          >
            <JoinSuccessPopup
              chapterTitle={chapterData?.chapterDetails?.title}
              onClickExplore={() => {
                mixpanel.track(
                  "Creator Chapter: Success Popup: Explore Button Clicked"
                );
                navigate("/influencer/dashboard");
              }}
            />
          </DialogPopup>
        )
      }

      <div className="w-full h-full min-h-screen bg-[#171717] text-white flex flex-col ">
        {/* Header */}
        <Header bg={"bg-[#171717]"} hideUser={!isLogin} hideName={true} />

        {/* Main */}
        <div className="flex-1">
          {hasChapter && !loading ? (
            <LBCHomePage
              isLogin={isLogin}
              chapterDetails={chapterData?.chapterDetails}
              influencers={chapterData?.joinedCreators?.influencers}
              chapterCover={
                <ChapterCover
                  chapterDetails={chapterData?.chapterDetails}
                  avatars={chapterData?.joinedCreators?.avatars}
                  totalCount={chapterData?.joinedCreators?.count}
                  onJoinChapter={() => {
                    mixpanel.track(
                      "Creator Chapter: Join Chapter Button Clicked"
                    );
                    handleJoinChapter();
                  }}
                  isJoined={chapterData?.isJoined}
                  loadingJoin={loadingJoin}
                  onShare={() => {
                    mixpanel.track("Creator Chapter: Invite Button Clicked");
                    handleShareChapter();
                  }}
                />
              }
              NotLoggedInCard={
                <NotLoggedInCard
                  userCity={chapterData?.chapterDetails?.city}
                  handleJoinChapter={handleJoinChapter}
                />
              }
            />
          ) : loading ? (
            <ChapterDetailsLoadingSkeleton />
          ) : (
            <div>Chapter Not Found</div>
          )}
        </div>

        {/* Footer */}
        <div className="w-full">
          <Container>
            <GradientDivider className="h-[1px]" />
            <Footer />
          </Container>
        </div>
      </div>
    </>
  );
};
